/**
 * Riga fattura passiva — dettaglio compatto + azioni stato + pagamenti.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, CreditCard, XCircle } from 'lucide-react';
import { fmtEUR } from '../../lib/contractModificationApi';
import SupplierInvoicePayments from './SupplierInvoicePayments';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem('token');
  return { headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

const STATUS_STYLE = {
  ricevuta: 'bg-slate-100 text-slate-800',
  registrata: 'bg-blue-100 text-blue-800',
  approvata: 'bg-emerald-100 text-emerald-800',
  contestata: 'bg-amber-100 text-amber-800',
  annullata: 'bg-red-100 text-red-800',
};

const NEXT_STATES = {
  ricevuta: [
    { target: 'registrata', label: 'Registra' },
    { target: 'contestata', label: 'Contesta' },
    { target: 'annullata', label: 'Annulla' },
  ],
  registrata: [
    { target: 'approvata', label: 'Approva' },
    { target: 'contestata', label: 'Contesta' },
    { target: 'annullata', label: 'Annulla' },
  ],
  approvata: [
    { target: 'contestata', label: 'Contesta' },
    { target: 'annullata', label: 'Annulla' },
  ],
  contestata: [
    { target: 'ricevuta', label: 'Ripristina' },
    { target: 'registrata', label: 'Registra' },
    { target: 'annullata', label: 'Annulla' },
  ],
  annullata: [],
};

const SupplierInvoiceRow = ({ invoice, onChanged }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  const transition = async (target) => {
    if (target === 'annullata' && !window.confirm('Annullare la fattura? L\'operazione è definitiva.')) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/supplier-invoices/${invoice.id}/transition`,
        { target_status: target },
        auth()
      );
      toast.success(`Fattura ${target}`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore transizione');
    } finally {
      setBusy(false);
    }
  };

  const computed = invoice.computed || {};

  return (
    <div className="border-b last:border-b-0" data-testid={`invoice-row-${invoice.id.slice(0, 8)}`}>
      <div className="grid grid-cols-12 gap-2 px-3 py-2 items-center text-sm hover:bg-slate-50">
        <div className="col-span-2">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-1 text-slate-700 hover:text-slate-900"
            data-testid={`invoice-expand-${invoice.id.slice(0, 8)}`}
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            <span className="font-medium">{invoice.invoice_number}</span>
          </button>
        </div>
        <div className="col-span-2 text-xs text-slate-600">
          <div>{invoice.invoice_date?.slice(0, 10)}</div>
          {invoice.due_date && (
            <div className="text-slate-400">scad. {invoice.due_date.slice(0, 10)}</div>
          )}
        </div>
        <div className="col-span-2 text-slate-700 text-xs truncate">
          {invoice.supplier_name || invoice.supplier_id}
        </div>
        <div className="col-span-2 text-right">{fmtEUR(invoice.subtotal_taxable)}</div>
        <div className="col-span-2 text-right">
          <div>{fmtEUR(computed.amount_paid || 0)}</div>
          <div className="text-xs text-slate-400">residuo {fmtEUR(computed.amount_remaining || 0)}</div>
        </div>
        <div className="col-span-2">
          <Badge className={STATUS_STYLE[invoice.status] || 'bg-slate-100'}>
            {invoice.status}
          </Badge>
        </div>
      </div>

      {expanded && (
        <div className="px-4 py-3 bg-slate-50 border-t space-y-3">
          {/* Testata dettagli */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <Field label="Regime fiscale" value={invoice.tax_regime} />
            <Field label="Ricezione" value={invoice.received_date?.slice(0, 10)} />
            <Field label="Aliquota IVA" value={`${invoice.vat_rate}%`} />
            <Field label="IVA esposta" value={fmtEUR(invoice.vat_amount)} />
            <Field label="Totale doc." value={fmtEUR(invoice.total_document)} />
            <Field label="Netto da pagare" value={fmtEUR(invoice.net_to_pay)} highlight />
            <Field label="SAL collegato" value={invoice.subcontract_sal_id ? invoice.subcontract_sal_id.slice(0, 8) : '—'} />
            <Field label="Note" value={invoice.notes || '—'} />
          </div>

          {/* Righe fattura */}
          <div>
            <div className="text-xs font-medium text-slate-500 mb-1">Righe fattura</div>
            <div className="rounded-md bg-white border overflow-hidden">
              {(invoice.lines || []).map((ln) => (
                <div key={ln.id} className="px-3 py-1.5 text-xs border-b last:border-b-0 grid grid-cols-12 gap-2">
                  <div className="col-span-6">{ln.description}</div>
                  <div className="col-span-1 text-right">{ln.quantity}</div>
                  <div className="col-span-2 text-right">{fmtEUR(ln.unit_price)}</div>
                  <div className="col-span-2 text-right font-medium">{fmtEUR(ln.net_amount)}</div>
                  <div className="col-span-1 text-slate-400 text-[10px]">
                    {ln.subcontract_item_id ? 'item' : ln.project_economic_line_id ? 'qe' : '—'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Azioni stato */}
          <div className="flex gap-2 flex-wrap">
            {(NEXT_STATES[invoice.status] || []).map((s) => (
              <Button
                key={s.target}
                size="sm"
                variant={s.target === 'annullata' ? 'destructive' : 'outline'}
                disabled={busy}
                onClick={() => transition(s.target)}
                data-testid={`invoice-transition-${s.target}-${invoice.id.slice(0, 8)}`}
              >
                {s.target === 'annullata' && <XCircle className="w-3 h-3 mr-1" />}
                {s.label}
              </Button>
            ))}
          </div>

          {/* Pagamenti */}
          {['registrata', 'approvata'].includes(invoice.status) && (
            <SupplierInvoicePayments invoice={invoice} onChanged={onChanged} />
          )}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, highlight }) => (
  <div>
    <div className="text-slate-400 uppercase tracking-wide text-[10px]">{label}</div>
    <div className={`text-slate-700 ${highlight ? 'font-semibold' : ''}`}>{value}</div>
  </div>
);

export default SupplierInvoiceRow;
